import React from 'react';
import { format } from 'date-fns';
import urlHelper from '../../../../utils/urlHelper';
import { showAppModal, hideAppModal } from '../../../../actions/appActions';
import store from '../../../../redux/store';

/**
 * @typedef {import('../types/ContentItems').ContentItem} ContentItem
 */

const hoursInMs = 3600000;
const minutesInMs = 60000;
const secondsInMs = 1000;
const unitHour = 'h';
const unitMinute = 'm';
const unitSecond = 's';
const unitMs = 'ms';

/**
 * Ensures the value is a valid number; returns 0 if not.
 * @param {any} value - The value to guard.
 * @param {number|null} maximum - Optional maximum to cap the value.
 * @returns {number} The original number or 0.
 */
const guardNumber = (value, maximum) => {
  value = typeof value === 'number' && !Number.isNaN(value) ? value : 0;
  value = Math.max(0, value);
  maximum = typeof maximum === 'number' && !Number.isNaN(maximum) ? maximum : null;

  return maximum === null ? value : Math.min(value, maximum);
};

/**
 * @typedef {Object} DurationFormatResult
 * @property {string} formatted - The full formatted duration string (e.g., "1h 2m 3s 456ms").
 * @property {number} raw - The original duration in milliseconds.
 * @property {number} hours - The number of whole hours.
 * @property {number} minutes - The number of whole minutes (after subtracting hours).
 * @property {number} seconds - The number of whole seconds (after subtracting hours and minutes).
 * @property {number} ms - The remaining milliseconds (after subtracting hours, minutes, and seconds).
 */

/**
 * @typedef {Object} FormatProps
 * @property {boolean} withUnits - Whether to include units in the output.
 * @property {number} decimalPlaces - Number of decimal places to include.
 * @property {string} unit - custom unit string
 * @property {number} leftPadding - number of characters to left pad the value with
 */

/**
 * Converts a duration to formatted time of a specific unit.
 * @param {number} duration - The duration in milliseconds.
 * @param {FormatProps} param - Formatting options.
 * @returns {string} The formatted duration and its components.
 * @private for internal use only, the params are not safeGuarded
 */
const timeInUnit = (duration, params) => {
  const { withUnits, unit, decimalPlaces, leftPadding } = params;
  const shouldShowDecimals = decimalPlaces > 0;
  const wholeNumber = Math.floor(duration);
  const decimalNumber = duration.toFixed(decimalPlaces);
  const number = shouldShowDecimals ? decimalNumber : wholeNumber;
  const padding =
    leftPadding > 0 ? '0'.repeat(Math.max(0, leftPadding - wholeNumber.toString().length)) : '';
  const unitStr = withUnits ? ` ${unit}` : '';

  return `${padding}${number}${unitStr}`;
};

const time = {
  unitHour,
  unitMinute,
  unitSecond,
  unitMs,
  /**
   * Formats a duration value into a human-readable string.
   * @param {number} duration - The duration in milliseconds
   * @returns {DurationFormatResult} The formatted duration and its components.
   */
  formatDuration(duration) {
    duration = guardNumber(duration);

    const raw = duration;
    const hours = Math.floor(duration / hoursInMs);

    duration -= hours * hoursInMs;
    const minutes = Math.floor(duration / minutesInMs);

    duration -= minutes * minutesInMs;
    const seconds = Math.floor(duration / secondsInMs);

    duration -= seconds * secondsInMs;
    const ms = duration;
    // no unit, 2 padding for hh:mm:ss
    const params1 = { withUnits: false, decimalPlaces: 0, leftPadding: 2 };
    // no unit, 3 padding, no decimals for ms
    const params2 = { withUnits: false, decimalPlaces: 0, leftPadding: 3 };
    const msPiece = ms > 0 ? `.${timeInUnit(ms, params2)}` : '';

    return {
      formatted: `${timeInUnit(hours, params1)}:${timeInUnit(minutes, params1)}:${timeInUnit(seconds, params1)}${msPiece}`,
      raw,
      hours,
      minutes,
      seconds,
      ms,
    };
  },

  /**
   * Converts a duration to whole hours and formats it.
   * @param {number} duration - The duration in milliseconds.
   * @param {FormatProps} param - Formatting options.
   * @returns {string} The formatted duration and its components.
   */
  inHours(duration, params = {}) {
    duration = guardNumber(duration);
    // defaults for hours
    const { withUnits = true, unit = unitHour, decimalPlaces = 2, leftPadding = 0 } = params;

    return timeInUnit(duration / hoursInMs, { withUnits, unit, decimalPlaces, leftPadding });
  },

  /**
   * Converts a duration to whole minutes and formats it.
   * @param {number} duration - The duration in milliseconds.
   * @param {FormatProps} param - Formatting options.
   * @returns {string} The formatted duration and its components.
   */
  inMinutes(duration, params = {}) {
    duration = guardNumber(duration);
    // defaults for minutes
    const { withUnits = true, unit = unitMinute, decimalPlaces = 2, leftPadding = 0 } = params;

    return timeInUnit(duration / minutesInMs, { withUnits, unit, decimalPlaces, leftPadding });
  },

  /**
   * Converts a duration to whole seconds and formats it.
   * @param {number} duration - The duration in milliseconds.
   * @param {FormatProps} param - Formatting options.
   * @returns {string} The formatted duration and its components.
   */
  inSeconds(duration, params = {}) {
    duration = guardNumber(duration);
    // defaults for seconds
    const { withUnits = true, unit = unitSecond, decimalPlaces = 2, leftPadding = 0 } = params;

    return timeInUnit(duration / secondsInMs, { withUnits, unit, decimalPlaces, leftPadding });
  },

  /**
   * Returns a millisecond duration formatted.
   * @param {number} duration - The duration in milliseconds.
   * @param {FormatProps} param - Formatting options.
   * @returns {string} The formatted duration and its components.
   */
  inMilliseconds(duration, params = {}) {
    duration = guardNumber(duration);
    // defaults for milliseconds
    const { withUnits = true, unit = unitMs, decimalPlaces = 0, leftPadding = 0 } = params;

    return timeInUnit(duration, { withUnits, unit, decimalPlaces, leftPadding });
  },

  /**
   * @param {string|Date} date - The date string or Date object to format.
   * @returns {string} The formatted date string in 'dd/MM/yyyy HH:mm' format.
   */
  formatDate(date) {
    if (typeof date === 'string') {
      date = new Date(date);
    } else if (date instanceof Date && Number.isNaN(date)) {
      console.error('Invalid date provided to formatDate:', date);

      return '-/-';
    }

    return format(date, 'dd/MM/yyyy HH:mm');
  },
};

/**
 * Opens a modal to preview the given content item.
 * @param {ContentItem} item
 */
const previewModal = item => {
  store.dispatch(
    showAppModal({
      show: true,
      content: 'PreviewModal',
      title: `Preview of: ${item.name}`,
      data: item,
      cancelFn: () => {
        store.dispatch(hideAppModal());
      },
    }),
  );
};

const asset = {
  /**
   * Generates a React element for the asset name, linking to its content URL if it has an ID.
   * @param {ContentItem} asset - The content item for which to generate the hyperlink.
   * @param {string} prefix - A prefix for the data-test attribute to uniquely identify the element in tests.
   * @param {boolean} previewEnabled - If true, clicking the link will open a preview modal instead of navigating to the URL.
   * @returns {JSX.Element} A React element containing the hyperlink.
   */
  getAssetNameEle(asset, prefix, previewEnabled = false) {
    if (!asset.id) {
      return <span data-test={`${prefix}-name`}>{asset.name}</span>;
    }

    return (
      <a
        className="button-link"
        target="_blank"
        rel="noopener noreferrer"
        href={urlHelper.getContentUrl(asset)}
        onClick={e => {
          e.stopPropagation();

          if (previewEnabled) {
            e.preventDefault();
            previewModal(asset);
          }
        }}
        data-test={`${prefix}-name`}
      >
        {asset.name}
      </a>
    );
  },

  /**
   * Formats the resolution of an asset as a string (e.g., "1920x1080").
   * @param {number} x - The width of the asset.
   * @param {number} y - The height of the asset.
   * @returns {string} The formatted resolution string.
   */
  formatResolution(x, y) {
    return `${x}x${y}`;
  },
};
const location = {
  /**
   * Removes leading / and *
   * @param {string} path
   * @return {string}
   */
  normalisePath(path) {
    return path.replace(/^\/+/, '').replace(/\*$/, '').replace(/\/+$/, '');
  },
  /**
   * Compares two paths, tells if path is within rootPath
   * @param {string} path
   * @param {string} rootPath
   * @return {boolean}
   */
  pathIsWithin(path, rootPath) {
    const normalizedPath = this.normalisePath(path);
    const normalizedRootPath = this.normalisePath(rootPath);

    // an empty root path means the filesystem root, which contains every path
    if (normalizedRootPath === '') {
      return true;
    }

    return (
      normalizedPath === normalizedRootPath || normalizedPath.startsWith(`${normalizedRootPath}/`)
    );
  },
};

export default {
  time,
  asset,
  location,
};
